"use client"

import { ChevronDownMini } from "@medusajs/icons"
import { Fragment, type ReactNode, useState } from "react"
import type { StoreProductMain, StoreRichTextDocument } from "../../types"
import styles from "./product-accordion.module.css"

type ProductAccordionProps = {
  items: StoreProductMain["accordion_items"]
}

type RichTextMark = {
  type?: string
  attrs?: Record<string, unknown>
}

type RichTextNode = {
  type?: string
  text?: string
  attrs?: Record<string, unknown>
  marks?: RichTextMark[]
  content?: RichTextNode[]
}

const renderMarkedText = (node: RichTextNode, key: string): ReactNode => {
  let content: ReactNode = node.text ?? ""

  for (const [markIndex, mark] of (node.marks ?? []).entries()) {
    const markKey = `${key}-mark-${markIndex}`

    if (mark.type === "bold") {
      content = <strong key={markKey}>{content}</strong>
    } else if (mark.type === "italic") {
      content = <em key={markKey}>{content}</em>
    } else if (mark.type === "strike") {
      content = <s key={markKey}>{content}</s>
    } else if (mark.type === "link" && typeof mark.attrs?.href === "string") {
      const target = mark.attrs.target === "_blank" ? "_blank" : "_self"
      content = (
        <a
          key={markKey}
          href={mark.attrs.href}
          target={target}
          rel={target === "_blank" ? "noreferrer" : undefined}
        >
          {content}
        </a>
      )
    }
  }

  return <Fragment key={key}>{content}</Fragment>
}

const renderNodes = (nodes: RichTextNode[] | undefined, keyPrefix: string): ReactNode =>
  nodes?.map((node, index) => {
    const key = `${keyPrefix}-${index}`
    const children = renderNodes(node.content, key)

    switch (node.type) {
      case "text":
        return renderMarkedText(node, key)
      case "paragraph":
        return <p key={key}>{children}</p>
      case "heading": {
        const level = Number(node.attrs?.level)
        const Tag = level === 2 ? "h2" : level === 3 ? "h3" : "h4"
        return <Tag key={key}>{children}</Tag>
      }
      case "bulletList":
        return <ul key={key}>{children}</ul>
      case "orderedList":
        return <ol key={key}>{children}</ol>
      case "listItem":
        return <li key={key}>{children}</li>
      case "blockquote":
        return <blockquote key={key}>{children}</blockquote>
      case "hardBreak":
        return <br key={key} />
      default:
        return <Fragment key={key}>{children}</Fragment>
    }
  }) ?? null

const renderRichText = (document: StoreRichTextDocument) =>
  renderNodes(document.content as RichTextNode[] | undefined, "rich")

export default function ProductAccordion({ items }: ProductAccordionProps) {
  const orderedItems = [...items]
    .filter((item) => item.title?.trim())
    .sort((left, right) => (left.position ?? 0) - (right.position ?? 0))
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  if (orderedItems.length === 0) {
    return null
  }

  return (
    <div className={styles.productAccordion}>
      {orderedItems.map((item, index) => {
        const isOpen = openIndex === index
        const itemId = item.id ?? String(index)
        const triggerId = `product-accordion-trigger-${itemId}`
        const panelId = `product-accordion-panel-${itemId}`

        return (
          <div
            key={item.id ?? `${item.position ?? index}-${item.title}`}
            className={styles.item}
            data-accordion-item={index}
            data-open={isOpen ? "true" : "false"}
          >
            <button
              id={triggerId}
              type="button"
              className={styles.trigger}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenIndex((current) => (current === index ? null : index))}
            >
              <span>{item.title}</span>
              <ChevronDownMini className={styles.chevron} aria-hidden="true" />
            </button>
            <div
              id={panelId}
              role="region"
              aria-label={item.title}
              aria-labelledby={triggerId}
              aria-hidden={!isOpen}
              className={styles.panel}
            >
              <div className={styles.panelInner}>{renderRichText(item.content)}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
